import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/admin/Layout';
import { Plus, Edit, Trash2, Globe } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { getCountryTranslations } from '../../translations/countryTranslations';

interface PartnerOffer {
  id: string;
  title: string;
  description: string;
  image_url: string;
  active: boolean;
  created_at: string;
  target_countries?: string[];
}

const PartnerOffers: React.FC = () => {
  const { t } = useTranslation();
  const [offers, setOffers] = useState<PartnerOffer[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<PartnerOffer | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: 'https://images.unsplash.com/photo-1542291026-7eec264c27ff',
    active: true,
    target_countries: [] as string[]
  });

  const countryOptions = getCountryTranslations('en').map(country => ({
    value: country.value,
    label: country.label
  }));

  useEffect(() => {
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('partner_offers')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setOffers(data || []);
    } catch (error) {
      console.error('Error fetching partner offers:', error);
      toast.error(t('errorFetchingData'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOffer = async () => {
    try {
      const { error: createError } = await supabase
        .from('partner_offers')
        .insert([{
          title: formData.title,
          description: formData.description,
          image_url: formData.image_url,
          active: formData.active,
          target_countries: formData.target_countries.length > 0 ? formData.target_countries : null
        }]);

      if (createError) throw createError;

      toast.success(t('partnerOfferCreated'));
      setShowCreateModal(false);
      fetchOffers();
    } catch (error) {
      console.error('Error creating partner offer:', error);
      toast.error(t('errorCreatingPartnerOffer'));
    }
  };

  const handleUpdateOffer = async () => {
    if (!selectedOffer) return;

    try {
      const { error: updateError } = await supabase
        .from('partner_offers')
        .update({
          title: formData.title,
          description: formData.description,
          image_url: formData.image_url,
          active: formData.active,
          target_countries: formData.target_countries.length > 0 ? formData.target_countries : null
        })
        .eq('id', selectedOffer.id);

      if (updateError) throw updateError;

      toast.success(t('partnerOfferUpdated'));
      setShowEditModal(false);
      fetchOffers();
    } catch (error) {
      console.error('Error updating partner offer:', error);
      toast.error(t('errorUpdatingPartnerOffer'));
    }
  };

  const handleDeleteOffer = async (offer: PartnerOffer) => {
    try {
      const { error } = await supabase
        .from('partner_offers')
        .delete()
        .eq('id', offer.id);

      if (error) throw error;

      toast.success(t('partnerOfferDeleted'));
      fetchOffers();
    } catch (error) {
      console.error('Error deleting partner offer:', error);
      toast.error(t('errorDeletingPartnerOffer'));
    }
  };

  return (
    <AdminLayout title={t('partnerOfferManagement')}>
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-gray-900">{t('partnerOffers')}</h3>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="h-5 w-5 mr-2" />
            {t('createPartnerOffer')}
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : offers.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {t('noPartnerOffers')}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {offers.map((offer) => (
              <div key={offer.id} className="border rounded-lg overflow-hidden shadow-sm">
                <div className="relative h-48 bg-gray-100">
                  <img
                    src={offer.image_url}
                    alt={offer.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = 'https://placehold.co/400x200/e2e8f0/64748b?text=Error+Loading+Image';
                    }}
                  />
                </div>
                <div className="p-5">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="text-lg font-medium text-gray-900">{offer.title}</h4>
                      <p className="text-sm text-gray-500">{offer.description}</p>
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    {offer.target_countries && offer.target_countries.length > 0 ? (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {offer.target_countries.map(code => (
                          <span key={code} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            <Globe className="h-3 w-3 mr-1" />
                            {code}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500 mb-2">
                        <Globe className="h-4 w-4 inline mr-1" />
                        {t('allCountries')}
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-4 flex justify-between items-center">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      offer.active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {offer.active ? t('active') : t('inactive')}
                    </span>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          setSelectedOffer(offer);
                          setFormData({
                            title: offer.title,
                            description: offer.description || '',
                            image_url: offer.image_url,
                            active: offer.active,
                            target_countries: offer.target_countries || []
                          });
                          setShowEditModal(true);
                        }}
                        className="text-yellow-600 hover:text-yellow-900"
                      >
                        <Edit className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteOffer(offer)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {showCreateModal ? t('createPartnerOffer') : t('editPartnerOffer')}
                    </h3>
                    <div className="mt-4">
                      <form>
                        <div className="mb-4">
                          <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                            {t('title')}
                          </label>
                          <input
                            type="text"
                            id="title"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>

                        <div className="mb-4">
                          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                            {t('description')}
                          </label>
                          <textarea
                            id="description"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            rows={3}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>

                        <div className="mb-4">
                          <label htmlFor="image_url" className="block text-sm font-medium text-gray-700 mb-1">
                            {t('imageUrl')}
                          </label>
                          <input
                            type="text"
                            id="image_url"
                            value={formData.image_url}
                            onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>

                        <div className="mb-4">
                          <label htmlFor="target_countries" className="block text-sm font-medium text-gray-700 mb-1">
                            {t('targetCountries')}
                          </label>
                          <Select
                            id="target_countries"
                            isMulti
                            options={countryOptions}
                            value={countryOptions.filter(option => 
                              formData.target_countries.includes(option.value)
                            )}
                            onChange={(selected) => {
                              setFormData({
                                ...formData,
                                target_countries: selected ? selected.map(option => option.value) : []
                              });
                            }}
                            className="basic-multi-select"
                            classNamePrefix="select"
                            placeholder={t('selectCountries')}
                            isClearable={true}
                          />
                          <p className="mt-1 text-sm text-gray-500">
                            {t('leaveEmptyForAllCountries')}
                          </p>
                        </div>

                        <div>
                          <label className="inline-flex items-center">
                            <input
                              type="checkbox"
                              checked={formData.active}
                              onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                              className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                            />
                            <span className="ml-2 text-sm text-gray-700">{t('active')}</span>
                          </label>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={showCreateModal ? handleCreateOffer : handleUpdateOffer}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {showCreateModal ? t('create') : t('update')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {t('cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default PartnerOffers;